// src/pages/inventoryTransfers/InventoryTransferDetailsPage.js

import React, { useEffect, useState, useContext, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { format } from 'date-fns';
import { HiArrowLeft, HiArrowPath, HiCheckBadge, HiClock, HiTruck } from 'react-icons/hi2';
import Layout from '../../components/Layout';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ErrorMessage from '../../components/common/ErrorMessage';
import useApi from '../../hooks/useApi';
import { AuthContext } from '../../contexts/AuthContext';

const InventoryTransferDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const api = useApi();
  const { settings } = useContext(AuthContext);

  const [transfer, setTransfer] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [transferRes, logsRes] = await Promise.all([
        api.get(`inventory-transfers/${id}`),
        api.get('inventory-log', { queryParams: { relatedDocId: id, noPagination: true, limit: 1000 } }),
      ]);

      setTransfer(transferRes?.data || transferRes);
      const rawLogs = Array.isArray(logsRes?.data) ? logsRes.data : (Array.isArray(logsRes?.data?.data) ? logsRes.data.data : []);
      setLogs(rawLogs);
    } catch (err) {
      setError(err?.message || 'Failed to load transfer details.');
    } finally {
      setLoading(false);
    }
  }, [api, id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const dispatchTransfer = async () => {
    if (!window.confirm(`Dispatch transfer ${transfer.transferNumber || transfer._id}?`)) return;
    setActionLoading(true);
    try {
      await api.post(`inventory-transfers/${transfer._id}/dispatch`, {});
      await loadData();
    } catch (err) {
      alert(err?.message || 'Dispatch failed.');
    } finally {
      setActionLoading(false);
    }
  };

  const completeTransit = async () => {
    if (!window.confirm(`Complete transit for transfer ${transfer.transferNumber || transfer._id}?`)) return;
    setActionLoading(true);
    try {
      await api.post(`inventory-transfers/${transfer._id}/receive`, {});
      await loadData();
    } catch (err) {
      alert(err?.message || 'Failed to complete transit.');
    } finally {
      setActionLoading(false);
    }
  };

  const currencySymbol = settings?.currencySymbol || '$';

  const timelineItems = [
    {
      key: 'created',
      title: 'Transfer Created',
      subtitle: transfer?.createdAt ? format(new Date(transfer.createdAt), 'MMM d, yyyy, h:mm a') : 'Pending',
      icon: HiClock,
      active: true,
    },
    {
      key: 'dispatched',
      title: 'Dispatched',
      subtitle: transfer?.dispatchedAt ? format(new Date(transfer.dispatchedAt), 'MMM d, yyyy, h:mm a') : 'Not dispatched yet',
      icon: HiTruck,
      active: Boolean(transfer?.dispatchedAt),
    },
    {
      key: 'completed',
      title: 'Transit Completed',
      subtitle: transfer?.receivedAt ? format(new Date(transfer.receivedAt), 'MMM d, yyyy, h:mm a') : 'Not completed yet',
      icon: HiCheckBadge,
      active: Boolean(transfer?.receivedAt),
    },
  ];

  if (loading) {
    return <Layout><LoadingSpinner /></Layout>;
  }

  if (error) {
    return <Layout><ErrorMessage message={error} onRetry={loadData} /></Layout>;
  }

  if (!transfer) {
    return <Layout><p>Transfer not found.</p></Layout>;
  }

  return (
    <Layout title="Inventory Transfer Details">
      <div className="max-w-6xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <button
            onClick={() => navigate('/inventory-transfers')}
            className="flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-gray-900"
          >
            <HiArrowLeft className="w-5 h-5" />
            Back to Transfers
          </button>
          <div className="flex items-center gap-2">
            {transfer.status === 'Pending' && (
              <button
                onClick={dispatchTransfer}
                disabled={actionLoading}
                className="inline-flex items-center gap-2 bg-emerald-600 text-white px-4 py-2 rounded-lg hover:bg-emerald-700 disabled:opacity-50"
              >
                <HiArrowPath className="w-5 h-5" />
                {actionLoading ? 'Processing...' : 'Dispatch Transfer'}
              </button>
            )}
            {transfer.status === 'In-Transit' && (
              <button
                onClick={completeTransit}
                disabled={actionLoading}
                className="inline-flex items-center gap-2 bg-emerald-600 text-white px-4 py-2 rounded-lg hover:bg-emerald-700 disabled:opacity-50"
              >
                <HiCheckBadge className="w-5 h-5" />
                {actionLoading ? 'Processing...' : 'Complete Transit'}
              </button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 bg-white rounded-lg shadow p-6 space-y-6">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-sm text-gray-500">Reference</p>
                <h1 className="text-2xl font-bold text-gray-900">{transfer.transferNumber || transfer._id}</h1>
                <p className="text-sm text-gray-600 mt-1">Status: <span className="font-semibold">{transfer.status}</span></p>
              </div>
              <div className="text-right text-sm text-gray-600">
                <p>Created</p>
                <p className="font-medium text-gray-900">{transfer.createdAt ? format(new Date(transfer.createdAt), 'PPP p') : 'N/A'}</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="rounded-lg border p-4 bg-gray-50">
                <p className="text-sm font-medium text-gray-500 mb-1">From</p>
                <p className="font-semibold text-gray-900">{transfer.fromName || transfer.fromLocation?.type || 'N/A'}</p>
                <p className="text-sm text-gray-600">{transfer.fromLocation?.type || '—'} ID: {transfer.fromLocation?.id || '—'}</p>
              </div>
              <div className="rounded-lg border p-4 bg-gray-50">
                <p className="text-sm font-medium text-gray-500 mb-1">To</p>
                <p className="font-semibold text-gray-900">{transfer.toName || transfer.toLocation?.type || 'N/A'}</p>
                <p className="text-sm text-gray-600">{transfer.toLocation?.type || '—'} ID: {transfer.toLocation?.id || '—'}</p>
              </div>
            </div>

            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-3">Transfer Items</h2>
              <div className="space-y-3">
                {(transfer.items || []).map((item) => (
                  <div key={item._id || `${item.product?._id || item.product}-${item.quantity}`} className="flex items-center justify-between rounded-lg border p-4">
                    <div>
                      <p className="font-medium text-gray-900">{item.product?.name || item.productName || 'Product'}</p>
                      <p className="text-sm text-gray-500">Qty: {item.quantity}</p>
                    </div>
                    <div className="text-sm text-gray-500">
                      {currencySymbol}0.00
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-3">Notes</h2>
              <div className="rounded-lg border bg-gray-50 p-4 text-sm text-gray-700">
                {transfer.notes || 'No notes provided.'}
              </div>
            </div>
          </div>

          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Timeline</h2>
              <div className="space-y-4">
                {timelineItems.map((step) => {
                  const Icon = step.icon;
                  return (
                    <div key={step.key} className="flex items-start gap-3">
                      <div className={`mt-1 rounded-full p-2 ${step.active ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-400'}`}>
                        <Icon className="w-4 h-4" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{step.title}</p>
                        <p className="text-sm text-gray-500">{step.subtitle}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Log Entries</h2>
              {logs.length === 0 ? (
                <p className="text-sm text-gray-500">No inventory logs found for this transfer yet.</p>
              ) : (
                <div className="space-y-3">
                  {logs.map((log) => (
                    <div key={log._id} className="rounded-lg border p-3 bg-gray-50">
                      <div className="flex items-center justify-between gap-2">
                        <p className="font-semibold text-gray-900">{log.type}</p>
                        <p className="text-xs text-gray-500">
                          {log.timestamp ? format(new Date(log.timestamp), 'MMM d, yyyy, h:mm a') : ''}
                        </p>
                      </div>
                      <p className="text-sm text-gray-600 mt-1">{log.productName || 'Product'} • Qty: {log.quantityChanged}</p>
                      <p className="text-xs text-gray-500 mt-1">{log.remarks || 'No remarks'}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default InventoryTransferDetailsPage;
